import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import type { SelectOption } from '@/components/Select';
import { Select } from '@/components/Select';
import type { OrderAppProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import { useRouter } from '@/hooks/useRouter';
import { useStrapSizeDatas } from '@/hooks/useStrapSizeDatas';
import { useStrapSizes } from '@/hooks/useStrapSizes';
import { paths } from '@/lib/paths';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe, NonEmptyArray } from '@/lib/types';

import type { SizeState } from '../ProductDetailSection';
import { SizesModal } from './components';
import { getSelectedSizeItem, sizeStateOptions } from './helper';

import * as M from './messages';
import * as S from './Sizes.styles';

export interface SizesProps {
  businessData: Maybe<OrderAppProductVariantsBusinessDetailsQuery>;
  setSizeState: Dispatch<SetStateAction<SizeState>>;
  sizeState: SizeState;
  sizesSkus: NonEmptyArray<string>;
  sku: string;
  strapId: string;
}

export const Sizes = ({
  setSizeState,
  sizeState,
  sizesSkus,
  sku,
  strapId,
}: SizesProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { push, query: routerQuery } = useRouter();
  const { formatMessage } = useIntl();
  const [strapSizeDatas, setStrapSizeDatas] = useState<
    Record<string, any> | undefined
  >();

  useStrapSizeDatas(setStrapSizeDatas, sku);
  useStrapSizes(
    sizesSkus,
    strapId,
    false,
    setSizeState,
    sizeState,
    strapSizeDatas,
  );

  const onSetSizesSelectedItemChange = (option: Maybe<SelectOption>) => {
    setSizeState((prevState) => ({ ...prevState, isLoadingOtherSize: true }));
    const slug = (option?.data as { id: string; sku: string })?.sku;
    if (slug) {
      const urlOpts = {
        ...routerQuery,
        sku: slug,
      };
      return push(paths.straps.material.product.url(urlOpts)).then(() => {
        setSizeState((prevState) => ({
          ...prevState,
          isLoadingOtherSize: false,
        }));
      });
    }
    setSizeState((prevState) => ({ ...prevState, sizesSelectedItem: option }));
  };
  const selectItemsOptions = sizeStateOptions(
    sizeState.selectItems,
    formatMessage,
  );
  const selectedSizeItem = getSelectedSizeItem(
    sizeState.sizesSelectedItem,
    formatMessage,
  );
  return (
    <S.Wrapper
      {...generateDataTestId('strapSizes', 'wrapper')}
      data-test-id='SizesWrapper'
    >
      <S.Text
        {...generateDataTestId('strapSizes', 'availableSizesText')}
        data-test-id='SizesAvailableSizesText'
      >
        {M.AvailableSizes}
      </S.Text>
      {sizeState.selectItems.length > 0 && (
        <S.SelectWrapper
          {...generateDataTestId('strapSizes', 'select')}
          data-test-id='SizesSelectWrapper'
        >
          <Select
            isSearchable={false}
            onChange={onSetSizesSelectedItemChange}
            options={selectItemsOptions}
            styles={{
              menu: (provided) => ({
                ...provided,
                width: 'auto',
              }),
            }}
            value={selectedSizeItem}
            variant='default'
          />
        </S.SelectWrapper>
      )}
      <S.SizeGuide
        {...generateDataTestId('strapSizes', 'sizeGuide')}
        data-test-id='SizesSizeGuide'
      >
        <Flex alignItems='center' onClick={() => setIsOpen(true)}>
          <S.Ruler
            {...generateDataTestId('strapSizes', 'rulerIcon')}
            data-test-id='SizesRulerIcon'
          />

          <LegacyButton
            label={M.SizeGuide}
            size='l'
            variant='link'
            width='auto'
          >
            <Icon name='chevronRight' size='s' />
          </LegacyButton>
        </Flex>
        <SizesModal isOpen={isOpen} setIsOpen={setIsOpen} />
      </S.SizeGuide>
    </S.Wrapper>
  );
};
